import { Injectable } from '@angular/core';
import {GoogleTagManagerService} from "angular-google-tag-manager";
import {Order, OrderItem, Product, ShoppingCart, ShoppingCartItem} from '../interfaces/DataModel';

@Injectable({
  providedIn: 'root'
})
export class GtmService {

  constructor(private gtmService: GoogleTagManagerService) {}

  private async resetEcommerce() {
    await this.gtmService.pushTag({ ecommerce: null });
  }

  async viewItem(product: Product) {
    await this.resetEcommerce();

    this.gtmService.pushTag({
      event: 'view_item',
      ecommerce: {
        items: [
          {
            item_id: product.id,
            item_name: product.name,
            price: parseFloat(product.lowestObject.price),
            currency: 'TRY'
          }
        ]
      }
    }).then();
  }

  async addToCart(shoppingCardItem: ShoppingCartItem) {
    await this.resetEcommerce();

    this.gtmService.pushTag({
      event: 'add_to_cart',
      ecommerce: {
        items: [
          {
            item_id: shoppingCardItem.productItem.id,
            item_name: shoppingCardItem.productItem.product.name,
            price: parseFloat(shoppingCardItem.productItem.price as any),
            quantity: shoppingCardItem.quantity,
            currency: 'TRY'
          }
        ]
      }
    }).then();
  }

  async removeFromCart(shoppingCardItem: ShoppingCartItem) {
    await this.resetEcommerce();

    this.gtmService.pushTag({
      event: 'remove_from_cart',
      ecommerce: {
        items: [
          {
            item_id: shoppingCardItem.productItem.id,
            item_name: shoppingCardItem.productItem.product.name,
            price: parseFloat(shoppingCardItem.productItem.price as any),
            quantity: shoppingCardItem.quantity,
            currency: 'TRY'
          }
        ]
      }
    }).then();
  }

  async beginCheckout(shoppingCard: ShoppingCart) {
    await this.resetEcommerce();

    this.gtmService.pushTag({
      event: 'begin_checkout',
      ecommerce: {
        currency: 'TRY',
        value: shoppingCard?.shoppingCartTotal ? parseFloat(shoppingCard?.shoppingCartTotal) : 0,
        items: shoppingCard?.shoppingCartItems.map((i: ShoppingCartItem) => ({
          item_id: i.productItem.id,
          item_name: i.productItem.product.name,
          price: parseFloat(i.productItem.price as any),
          quantity: i.quantity,
        }))
      }
    }).then();
  }

  async addPaymentInfo(shoppingCard: ShoppingCart, paymentType: string) {
    await this.resetEcommerce();

    this.gtmService.pushTag({
      event: 'add_payment_info',
      ecommerce: {
        currency: 'TRY',
        payment_type: paymentType,
        items: shoppingCard.shoppingCartItems.map((i: ShoppingCartItem) => ({
          item_id: i.productItem.id,
          item_name: i.productItem.product.name,
          price: parseFloat(i.productItem.price as any),
          quantity: i.quantity,
        }))
      },
    }).then();
  }

  async addShippingInfo(shoppingCard: ShoppingCart, shippingMethod: string) {
    await this.resetEcommerce();

    this.gtmService.pushTag({
      event: 'add_shipping_info',
      ecommerce: {
        currency: 'TRY',
        shipping_tier: shippingMethod,
        value: parseFloat(shoppingCard.shoppingCartTotal),
        items: shoppingCard.shoppingCartItems.map((i: ShoppingCartItem) => ({
          item_id: i.productItem.id,
          item_name: i.productItem.product.name,
          price: parseFloat(i.productItem.price as any),
          quantity: i.quantity,
        }))
      }
    }).then();
  }

  async purchase(order: Order) {
    await this.resetEcommerce();

    this.gtmService.pushTag({
      event: 'purchase',
      ecommerce: {
        transaction_id: order.code,
        value: parseFloat(order.price as any),
        tax: parseFloat(order.taxAmount),
        shipping: parseFloat(order.deliveryAmount),
        currency: 'TRY',
        items: order.orderItems.map((i: OrderItem) => ({
          item_id: i.id,
          item_name: i.name,
          price: parseFloat(i.price),
          quantity: i.quantity
        }))
      }
    }).then();
  }
}
